<template>
  <!-- イベントタイトル -->
  <div class="u-containar responsive-head">
    <div>
      <h2 class="eventsHeader_heading">{{ event.title }}</h2>
      <div class="eventsHeader">
        <ul class="eventsHeader_label">
          <li :class="eventTypeClass">{{ mainEventLabel }}</li>
          <li v-for="castType in event.cast_type" :key="castType.value">
            {{ castType.label }}
          </li>
          <li v-for="castType in event.filter_type" :key="castType.value">
            {{ castType.label }}
          </li>
        </ul>
        <p class="eventsHeader_date">
          {{ getDateToWeek(event.term_start) }}
        </p>
      </div>
      <template v-if="!event.isArchived">
        <p
          v-if="event.type === 'オンライン' && event.place_name"
          class="events_place -online"
        >
          {{ event.place_name }}
        </p>
        <p
          v-else-if="event.type === 'オフライン'"
          class="events_place -offline"
        >
          {{ getPlaceInfo(event) }}
        </p>
      </template>
      <ul class="tags">
        <li
          v-for="tag in event.isArchived ? event.archiveTags : event.tags"
          :key="tag"
        >
          <Tag :text="tag" check :icon="event.isArchived ? 'tag' : ''" />
        </li>
      </ul>
    </div>
    <img v-if="event.thumbnail" :src="event.thumbnail" alt="" />
    <img v-else src="@/assets/images/common/noimg.jpg" alt="" />
  </div>

  <!-- イベントnav -->
  <div class="u-containar">
    <div class="responsive-nav" ref="eventButtonWrapper">
      <div
        class="button-wrapper"
        v-if="!event.isArchived"
        :class="{ fixed: state.spEventButtonFixed }"
      >
        <EventButton
          :id="$route.params.id"
          :link="event.entry_link"
          :term_end="event.term_end"
          :entry_start="event.entry_start"
          :entry_end="event.entry_end"
          :is_subscribed="isSubscribed"
          :is_fixed="state.spEventButtonFixed"
        />
      </div>
      <LocalNav :list="localNav" :offset="30" class="localNav" />
    </div>
  </div>

  <!-- イベント概要 -->
  <div class="u-containar">
    <div class="responsive-contents">
      <!-- 画面左部 -->
      <div class="responsive-contents_main">
        <section id="about">
          <div class="eventsDetail_heading">
            <HeadingLv2 text="イベント概要" bgBlue />
          </div>
          <div class="eventsDetail_about">
            <dl>
              <dt>イベント名</dt>
              <dd>{{ event.title }}</dd>
            </dl>
            <dl>
              <dt>開催日時</dt>
              <dd>
                {{ getDateToTime(event.term_start) }}
                〜
                {{ dayjs(event.term_end).format('H時mm分') }}
              </dd>
            </dl>
            <template v-if="!event.isArchived">
              <dl v-if="event.type === 'オンライン'">
                <dt>参加方法</dt>
                <dd>
                  <p v-if="event.place_name">{{ event.place_name }}</p>
                  <div class="eventPrecautionForJoin_wrap" v-if="hasZoomUrl">
                    <EventPrecautionForJoin
                      :precautionHtml="event.place_precaution"
                    />
                  </div>
                </dd>
              </dl>
              <template v-if="event.type === 'オフライン'">
                <dl>
                  <dt>会場</dt>
                  <dd>
                    {{ getPlaceInfo(event) }}
                  </dd>
                </dl>
                <dl>
                  <dt>会場住所</dt>
                  <dd>
                    {{ getPlaceAddress(event) }}
                    <div class="eventsDetail_about_btn">
                      <a :href="event.place_mapUrl" target="_blank">
                        地図をひらく
                      </a>
                    </div>
                  </dd>
                </dl>
              </template>
              <dl v-if="event.reception">
                <dt>受付開始時間</dt>
                <dd v-html="event.reception" />
              </dl>
              <dl v-if="event.remarks">
                <dt>備考</dt>
                <dd v-html="event.remarks" />
              </dl>
            </template>
          </div>

          <section v-if="isShowHospitalListLayout">
            <EventDetailHospital
              :hospitals="event.hospitals"
              :isArchived="event.isArchived"
              :openModal="openModal"
            />
          </section>
        </section>

        <EventDetailOnlineLive
          v-if="isShowOnlineLiveLayout"
          :event="event"
          :hospitals="eventHospitals"
          :isNoEntry="isNoEntry"
          :isSubscribed="isSubscribed"
          :getDateToWeek="getDateToWeek"
          :eventDate="getFormattedEventDate(event.term_start)"
        />

        <EventDetailBenefits
          v-if="event.benefits.length"
          :benefits="event.benefits"
        />
        <EventDetailSeminars
          v-if="event.seminars.length"
          :seminars="event.seminars"
          :isArchived="event.isArchived"
          :openModal="openModal"
        />
        <EventUse v-if="!event.isArchived" :eventType="event.type" />
        <div class="button-wrapper-sp hiddenPc" v-if="!event.isArchived">
          <EventButton
            :id="$route.params.id"
            :link="event.entry_link"
            :term_end="event.term_end"
            :entry_start="event.entry_start"
            :entry_end="event.entry_end"
            :is_subscribed="isSubscribed"
          />
        </div>
      </div>
      <!-- 画面右部 -->
      <div class="hiddenSp">
        <div class="responsive-contents_sub">
          <div class="card">
            <h2>{{ event.title }}</h2>
            <template v-if="!event.isArchived">
              <div class="categories">
                <div class="label" :class="eventTypeClass">
                  {{ mainEventLabel }}
                </div>
                <div>{{ getDateToWeek(event.term_start) }}</div>
              </div>
              <p
                v-if="event.type === 'オンライン' && event.place_name"
                class="events_place -online"
              >
                {{ event.place_name }}
              </p>
              <p
                v-if="event.type === 'オフライン'"
                class="events_place -offline"
              >
                {{ getPlaceInfo(event) }}
              </p>
            </template>
            <ul class="tags">
              <li
                v-for="tag in event.isArchived ? event.archiveTags : event.tags"
                :key="tag"
              >
                <Tag :text="tag" check :icon="event.isArchived ? 'tag' : ''" />
              </li>
            </ul>
            <EventButton
              v-if="!event.isArchived"
              class="main-right-button"
              :id="$route.params.id"
              :link="event.entry_link"
              :term_end="event.term_end"
              :entry_start="event.entry_start"
              :entry_end="event.entry_end"
              :is_subscribed="isSubscribed"
            />
          </div>
          <LocalNavSide :items="localNav" :offset="30" />
        </div>
      </div>
    </div>
  </div>

  <section class="eventsSchedule">
    <div class="schedule-title-container">
      <div class="u-containar schedule-title">
        <HeadingLv2 text="近日開催予定" />
      </div>
    </div>
    <div class="schedule-card">
      <div class="u-containar cards">
        <EventCardActive v-for="(e, i) in recentEvents" :key="i" :event="e" />
      </div>
    </div>
  </section>

  <div class="u-containar hiddenSp">
    <!-- <EventDetailReport /> -->
    <EventDetailHelp />
  </div>

  <MovieModal
    v-model="state.modal"
    :movies="state.selectedMovies"
    :title="state.modalTitle"
  />
</template>

<script lang="ts">
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  computed,
  defineComponent,
  reactive,
  watch,
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'
import { useEvent } from '@/provider/event'
import { getEventTerm } from '@/utils/commonMethods' // eslint-disable-line
import LocalNav from '@/components/block/LocalNav.vue'
import LocalNavSide from '@/components/block/LocalNavSide.vue'
import EventUse from '@/components/block/EventUse.vue'
import EventButton from '@/components/block/EventButton.vue'
import EventDetailHospital from '@/components/block/EventDetailHospital.vue'
import EventDetailOnlineLive from '@/components/block/EventDetailOnlineLive.vue'
import EventDetailSeminars from '@/components/block/EventDetailSeminars.vue'
import EventDetailBenefits from '@/components/block/EventDetailBenefits.vue'
import EventDetailHelp from '@/components/block/EventDetailHelp.vue'
import EventPrecautionForJoin from '@/components/block/EventPrecautionForJoin.vue'
import MovieModal from '@/components/block/MovieModal.vue'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import Tag from '@/components/elements/Tag.vue'
import EventCardActive from '@/components/block/EventCardActive.vue'
import { EventDetailHospitalMovie } from '@/types/EventDetail'
import 'dayjs/locale/ja'
dayjs.locale('ja')
dayjs.extend(isBetween)

interface State {
  hospitalMoreHidden: boolean
  modal: boolean
  selectedMovies: EventDetailHospitalMovie[]
  modalTitle: string
  spEventButtonFixed: boolean
}

export default defineComponent({
  name: 'EventsDetail',

  components: {
    EventButton,
    EventDetailHospital,
    EventDetailOnlineLive,
    EventDetailSeminars,
    EventDetailBenefits,
    EventDetailHelp,
    EventPrecautionForJoin,
    // EventDetailReport,
    LocalNav,
    LocalNavSide,
    EventUse,
    EventCardActive,
    Tag,
    HeadingLv2,
    MovieModal
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const {
      event,
      getEvent,
      getEvents,
      loadEventHospitals,
      recentEvents,
      eventHospitals,
      isNoEntry
    } = useEvent()
    getEvents(4)

    const mainEventLabel = computed(() =>
      event.value.category?.name === 'キャリアガイダンス'
        ? 'キャリアガイダンス'
        : event.value.type
    )
    const eventTypeClass = computed(() => {
      if (mainEventLabel.value === 'キャリアガイダンス') return 'isCareer'
      if (mainEventLabel.value === 'オンライン') return 'isOnline'
      if (mainEventLabel.value === 'オフライン') return 'isOffline'
    })

    const state = reactive<State>({
      hospitalMoreHidden: false,
      modal: false,
      selectedMovies: [],
      modalTitle: '',
      spEventButtonFixed: false
    })

    watch(
      () => route.params.id,
      () => {
        if (route.name === 'EventsDetail') {
          ;(async () => {
            store.commit('showLoading')
            await getEvent(Number(route.params.id))
            store.commit('closeLoading')
            console.log('eventValue:', event.value)

            const registerEvents = {
              name: event.value.title,
              date: getEventTerm(event.value.term_start, event.value.term_end),
              place:
                event.value.type === 'オンライン'
                  ? 'WEB会議ツールによるオンライン参加'
                  : event.value.place_name,
              holding_type: event.value.type === 'オンライン' ? 1 : 2
            }
            console.log('setRegisterEvent', registerEvents)
            store.commit('setRegisterEvent', registerEvents)

            if (
              !event.value.programs_old_layout_flag &&
              !event.value.isArchived &&
              event.value.type === 'オンライン'
            ) {
              loadEventHospitals()
            }
          })()
        }
      },
      { immediate: true }
    )

    const isFinifh = (seminar: any) => {
      const isExpired = dayjs().isAfter(dayjs(seminar.term.end))
      return seminar.finish || isExpired
    }

    // 申し込み済みかどうか判定
    const isSubscribed = computed((): boolean => {
      if (!store.state.isLogin) return false

      const index: number = store.state.userInfo.event_history.findIndex(
        (item: { id: number; event_id: string }) =>
          item.event_id == route.params.id
      )

      return index > -1 ? true : false
    })

    // 配信イベントにzoom配信のものがあるか判定
    const hasZoomUrl = computed(() => {
      let flag = false
      event.value.programs?.forEach((program: any) => {
        if (flag) return
        if (program.item && program.item.length)
          flag = program.item.some((el: any) => el.zoom_url)
      })
      return flag
    })

    const getDateToWeek = (date: string) =>
      dayjs(date).format('YYYY年M月D日（ddd）')
    const getDateToTime = (date: string) =>
      dayjs(date).format('YYYY年M月D日（ddd）H時mm分')
    const getFormattedEventDate = (date: string) =>
      dayjs(date).format('YY/MM/DD')
    const getPlaceInfo = (event: any) =>
      `${event.place_name}(${event.place_prefecture}${event.place_city})`
    const getPlaceAddress = (event: any) =>
      `〒${event.place_zip} ${event.place_prefecture}${event.place_city}${event.place_town}`

    // 旧レイアウトを利用
    const isShowHospitalListLayout = computed((): boolean => {
      if (!Object.keys(event.value).length) return false

      return (
        event.value.programs_old_layout_flag ||
        event.value.isArchived ||
        event.value.type === 'オフライン'
      )
    })

    // 新レイアウトを利用
    const isShowOnlineLiveLayout = computed((): boolean => {
      if (!Object.keys(event.value).length) return false

      return (
        !event.value.programs_old_layout_flag &&
        !event.value.isArchived &&
        event.value.type === 'オンライン'
      )
    })

    // ローカルナビ
    const localNav = computed(() => {
      return [
        { id: 'about', label: 'イベント概要' },
        { id: 'hospital', label: '参加病院一覧', onlyArchive: true },
        { id: 'schedule', label: 'タイムスケジュールと参加病院' },
        { id: 'hospitalOnlineEvent', label: '参加病院', hideArchive: true },
        { id: 'benefits', label: '参加特典' },
        { id: 'seminar', label: 'セミナー紹介' },
        { id: 'use', label: '利用について', hideArchive: true }
      ].filter((nav: any) => {
        if (nav.id === 'benefits' && !event.value.benefits?.length) return false
        if (nav.id === 'seminar' && !event.value.seminars?.length) return false
        if (nav.id === 'schedule' && !isShowOnlineLiveLayout.value) return false
        if (nav.id === 'hospitalOnlineEvent' && !isShowOnlineLiveLayout.value)
          return false
        // アーカイブの時は非表示
        if (nav.hideArchive && event.value.isArchived) return false
        // アーカイブの時だけ表示
        if (nav.onlyArchive && !event.value.isArchived) return false
        return true
      })
    })

    // イベントボタン固定
    const eventButtonWrapper = ref<HTMLElement | null>(null)
    const handleScroll = () => {
      if (!event.isArchived && eventButtonWrapper.value) {
        const scrollPos = window.scrollY
        const buttonPos =
          eventButtonWrapper.value.getBoundingClientRect().top + window.scrollY
        if (scrollPos >= buttonPos) {
          state.spEventButtonFixed = true
        } else {
          state.spEventButtonFixed = false
        }
      }
    }
    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    const toReserve = () => {
      router.push({ path: '/events/reserve' })
    }

    const toEventTop = () => {
      router.push({ path: '/events' })
    }

    const openModal = async (
      movies: EventDetailHospitalMovie[],
      title: string
    ) => {
      const isLogin = await store.dispatch('checkLogin')
      if (!isLogin) return
      state.selectedMovies = movies
      state.modalTitle = title
      state.modal = true
    }

    return {
      isFinifh,
      isSubscribed,
      recentEvents,
      getDateToWeek,
      getDateToTime,
      getFormattedEventDate,
      event,
      eventHospitals,
      isNoEntry,
      toReserve,
      toEventTop,
      openModal,
      localNav,
      eventButtonWrapper,
      state,
      dayjs,
      getPlaceInfo,
      getPlaceAddress,
      isShowHospitalListLayout,
      isShowOnlineLiveLayout,
      hasZoomUrl,
      mainEventLabel,
      eventTypeClass
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

img {
  border-radius: 2px;
}
.responsive-head {
  margin-top: 30px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @include mixins.media() {
    gap: 40px;
    grid-template-columns: 1fr minmax(auto, 430px);
  }
}
.responsive-nav {
  margin: 30px -16px;
  @include mixins.media() {
    display: grid;
    grid-template: 'nav button';
    grid-template-columns: 1fr 215px;
    margin: 50px 0;
    box-shadow: 0 6px 4px -4px rgba(0, 0, 0, 0.08);
  }
  > .button-wrapper {
    grid-area: button;
    @include mixins.mediaMax() {
      background-color: var.$color-other-bg;
      padding: 20px;
      &.fixed {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 10;
        padding: 12px;
      }
    }
  }
  > .localNav {
    grid-area: nav;
    margin: 0;
  }
}
.button-wrapper-sp {
  background-color: var.$color-other-bg;
  padding: 20px;
  margin: 50px -16px 0;
}
::v-deep .localNav_list {
  display: flex;
}
.responsive-contents {
  @include mixins.media() {
    justify-content: space-between;
    display: flex;
  }

  &_main {
    @include mixins.media() {
      flex-basis: calc(100% - 282px);
      max-width: calc(100% - 282px);
    }
  }

  &_sub {
    width: 242px;
    position: sticky;
    top: 40px;
    .main-right-button {
      letter-spacing: 0;
      margin-top: 20px;
    }
  }
}

.card {
  border: 1px solid var.$color-border-gray;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  padding: 20px 20px 25px;
  margin-bottom: 30px;
  h2 {
    font-weight: 300;
    font-size: 1.6rem;
    letter-spacing: 0.02rem;
  }
  .categories {
    font-size: 1rem;
    letter-spacing: 0;
    margin: 10px 0;
    font-weight: 500;
    .label {
      display: inline-block;
      padding: 0 5px;
      margin-bottom: 5px;
      &.isOnline {
        background-color: #36c0f2;
        color: #fff;
      }
      &.isOffline {
        background-color: var.$color-primary;
        color: #fff;
      }
      &.isCareer {
        background-color: var.$color-main;
        color: #fff;
      }
    }
  }
  .tags {
    font-size: 1rem;
    letter-spacing: 0;
  }
}
.tags {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  font-weight: 500;
  > li {
    margin-right: 3px;
    margin-bottom: 3px;
  }
}
.seminarCassette {
  & + & {
    margin: 0;
  }
}
.eventsHeader {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 15px;
  line-height: 1;
}

.eventsHeader_label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 10px;

  li {
    display: inline-block;
    padding-right: 5px;
    padding-left: 5px;
    background-color: var.$color-secondary;
    font-size: 1.2rem;
    line-height: 21px;
    margin-top: 5px;
    margin-right: 8px;
    &.isOnline {
      background-color: #36c0f2;
      color: #fff;
    }
    &.isOffline {
      background-color: var.$color-primary;
      color: #fff;
    }
    &.isCareer {
      background-color: var.$color-main;
      color: #fff;
    }
  }
}

.eventsHeader_date {
  margin-left: auto;
  margin-top: 5px;
  margin-bottom: 10px;
  line-height: 21px;
}
.eventsHeader_heading {
  font-size: 2.2rem;
  font-weight: 500;
  line-height: 1.3;
}

.events_place {
  margin-top: 12px;
  display: flex;
  align-items: baseline;
  line-height: 1.5;

  &::before {
    flex-shrink: 0;
    display: block;
    content: '';
    margin-right: 10px;
  }

  &.-online::before {
    flex-basis: 18px;
    width: 18px;
    height: 10px;
    background-image: url('~@/assets/images/icon/videocam.svg');
    background-size: 18px 10px;
  }

  &.-offline::before {
    flex-basis: 10px;
    width: 10px;
    height: 16px;
    background-image: url('~@/assets/images/icon/ping-main.svg');
    background-size: 10px 16px;
    transform: translateY(3px);
  }
}

.no-padding {
  padding: 0;
}

.eventsDetail_about {
  margin-top: 20px;
  margin-bottom: 45px;
  line-height: 1.5;
  dl {
    @include mixins.media() {
      display: flex;
    }
    & + dl {
      margin-top: 20px;
      @include mixins.media() {
        margin-top: 10px;
      }
    }
  }
  dt {
    font-weight: 500;
    @include mixins.media() {
      flex-basis: 100px;
      flex-shrink: 0;
      margin-right: 1em;
    }
  }
  dd {
    margin-top: 8px;
    @include mixins.media() {
      margin-top: 0;
      flex-grow: 1;
    }
    & ::v-deep a {
      color: var.$color-main-text;
    }
  }
}

.eventsDetail_about_btn {
  margin-top: 10px;
  margin-bottom: 10px;

  a {
    display: inline-flex;
    align-items: center;
    height: 40px;
    line-height: 40px;
    background-color: var.$color-white;
    border: 1px solid var.$color-border-gray;
    border-radius: 10px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
    font-size: 1.4rem;
    font-weight: 500;
    text-align: center;
    outline: none;
    padding-left: 16px;
    padding-right: 16px;
    transition: 0.2s;

    &::before {
      content: '';
      flex-basis: 10px;
      flex-shrink: 0;
      width: 10px;
      height: 16px;
      background-image: url('~@/assets/images/icon/ping-main.svg');
      background-size: 10px 16px;
      margin-right: 8px;
      transform: translateY(1px);
    }

    &::after {
      content: '';
      display: inline-block;
      width: 8px;
      height: 8px;
      margin-left: 16px;
      border-top: 2px solid #29ccc3;
      border-right: 2px solid #29ccc3;
      transform: rotate(45deg);
    }
  }
}

.section {
  margin-top: 48px;
}

.localNav {
  margin-bottom: 20px;
  box-shadow: none;
}

.eventsSchedule {
  margin-top: 70px;
}
.schedule-title-container {
  background-color: var.$color-sub-bg-g;
  padding: 10px 0;
  @include mixins.media() {
    padding: 40px 0 0;
  }
  .headingLv2 {
    font-size: 2rem;
  }
}
.schedule-card {
  padding: 20px 0 0;
  @include mixins.media() {
    padding: 20px 0 40px;
    background-color: var.$color-sub-bg-g;
  }
}
.cards {
  display: grid;
  gap: 30px;
  @include mixins.media() {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr;
  }
}

.pre {
  white-space: pre-line;
}

.eventPrecautionForJoin_wrap {
  margin: 16px 0 8px;
  @include mixins.media() {
    margin-left: 0;
  }
}
</style>
